import useTranslation from 'next-translate/useTranslation';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';

// import html2canvas from 'html2canvas';
import { WeeeIcon } from '@/components/common/Icon';

import { getShareReferralImage } from '@/api/growth';
import bgImage from '@/assets/images/referral/landing-bg.png';
import Popup from '@/components/common/Popup';
import useLazy from '@/hooks/useLazy';
import logger from '@/utils/logger';
import classNames from 'classnames';
import Trans from 'next-translate/Trans';
import Loading from '../../Loading';
import { isAndroid } from '@/utils/common/device';
import { loadImageForAndroid } from '@/utils/loadImageForAndroid';

type Props = {
  onClose?: () => void;
  dom?: React.ReactChild;
  extraParams?: any;
};

const ShareReferralImage = forwardRef((props: Props, ref) => {
  const { dom } = props;
  const { t } = useTranslation('common');
  const [url, setUrl] = useState<string>('');
  const [loaded, setLoaded] = useState<boolean>(false);
  const [shareReferralImageData, setShareReferralImageData] = useState<any>({});

  const { show, hide, visible } = useLazy();
  useImperativeHandle(ref, () => {
    return {
      show,
      hide
    };
  });

  const getSaveImageData = async () => {
    try {
      const res = await getShareReferralImage(props?.extraParams || {});
      if (res?.result) {
        setShareReferralImageData(res?.object);
        setTimeout(() => {
          createImage();
        }, 5000);
      }
    } catch (err) {
      logger.error({
        page: window.location.href,
        err
      });
    }
  };

  const createImage = async () => {
    const element = document.getElementById('share-image-text');
    if (element) {
      // 安卓 WebView 特殊处理
      if (isAndroid()) {
        console.log('使用安卓专用处理方案');

        // 预处理所有图片
        const images = element.querySelectorAll('img');
        const imagePromises = Array.from(images).map(async img => {
          try {
            const processedSrc = await loadImageForAndroid(img.src);
            img.src = processedSrc;
          } catch (error) {
            console.warn('安卓图片处理失败:', error);
          }
        });

        await Promise.all(imagePromises);

        // 安卓 WebView 需要更长的等待时间
        await new Promise(resolve => setTimeout(resolve, 3000));
      }
      import('html2canvas')
        .then(({ default: html2canvas }) => {
          html2canvas(element, {
            // proxy: '/_next/proxyImg',
            useCORS: true,
            allowTaint: false,
            scrollY: 0,
            // 增加超时时间到 30 秒
            imageTimeout: 30000,
            // 添加重试次数
            removeContainer: true,
            backgroundColor: null
          }).then(canvas => {
            const img = canvas.toDataURL();
            setUrl(img);
          });
        })
        .catch(err => {
          console.log(err);
        });
    }
  };

  const saveImageContent = (
    <div
      id="share-image-text"
      className={classNames(
        'fixed top-[-2000px] left-[50%] translate-x-[-50%] w-[300px] h-[386px] bg-[#efeff4] rounded-500'
      )}>
      <div className="relative w-full h-full mx-auto my-0 p-0 bg-surface-100-bg rounded-600">
        <div className="absolute top-0 left-0 right-0">
          <img
            className="w-full object-cover absolute top-0 left-0"
            crossOrigin="anonymous"
            src={bgImage}
            alt="referral background image"
          />
        </div>
        <div className="text-center w-[300px] mx-auto my-0">
          <div className="relative top-[158px] text-center">
            <div className="mx-auto my-0">
              <div className="enki-body-3xs text-center text-primary-1">
                <Trans
                  i18nKey={'common:share_referral_coupon_subtitle'}
                  components={{
                    div: <div>{shareReferralImageData?.alias}</div>
                  }}
                />
              </div>
              <div
                className="mt-1 mx-auto max-w-[80%] enki-body-2xs-strong text-primary-1"
                dangerouslySetInnerHTML={{ __html: t('share_referral_coupon_detail') }}></div>
            </div>
          </div>
          <img
            crossOrigin="anonymous"
            alt="product image"
            className="absolute left-[50%] translate-x-[-50%] bottom-6 w-[130px] h-[130px] rounded-300"
            src={shareReferralImageData?.qrcode_url}
          />
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    visible && getSaveImageData();
  }, [visible]);

  return (
    <Popup
      {...props}
      show={visible}
      zIndex={1003}
      onClose={props?.onClose}
      destroyOnClose
      position="center"
      animateTime={0}
      wrapperClassName="bg-transparent p-0 text-center !shadow-none">
      {loaded && (
        <div
          style={{ width: '300px' }}
          className="relative mb-4 mx-auto text-center text-reserved-true-white enki-body-2xs !leading-[22px]">
          <span className="mx-4">{t('share_image_popup_title')}</span>
          <WeeeIcon
            type="iconcolor-close"
            className="absolute top-0 -right-5 w-6 h-6 bg-[#ccc] text-reserved-true-white rounded-400 leading-6 cursor-pointer"
            onClick={() => {
              props.onClose?.();
            }}
          />
        </div>
      )}
      {!!dom ? dom : saveImageContent}
      {!loaded && (
        <div className="mt-[180px] mx-auto">
          <Loading className="w-[60px] h-[60px]" />
        </div>
      )}
      <div style={{ width: '300px' }} className="my-0 mx-auto text-[0px] rounded-500 overflow-hidden">
        {url && (
          <img
            className="w-full"
            src={url}
            alt="invite-friends-image"
            onLoad={() => {
              setLoaded(true);
            }}
          />
        )}
      </div>
    </Popup>
  );
});

export default ShareReferralImage;
